import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import EventModal from '../components/EventModal';
import LoadingSpinner from '../components/LoadingSpinner';
import './CalendarPage.css';
import AppIcon from '../components/icons/AppIcon';

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day, agenda
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dayViewDate, setDayViewDate] = useState(new Date());
  const [projects, setProjects] = useState([]);

  // Carregar company_id do usuário
  useEffect(() => {
    const loadCompanyId = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        setCompanyId(data.company_id);
      } catch (error) {
        console.error('Erro ao carregar company_id:', error);
      }
    };
    
    loadCompanyId();
  }, [user]);

  // Carregar eventos e projetos
  useEffect(() => {
    if (companyId) {
      loadEvents();
      loadProjects();
    }
  }, [companyId]);

  // Realtime - Sincronização automática
  useEffect(() => {
    if (!companyId) return;

    console.log('🔴 Configurando Realtime para calendário');

    const channel = supabase
      .channel(`calendar_events:${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calendar_events',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('🔴 Realtime - Evento alterado:', payload);
          loadEvents();
        }
      )
      .subscribe();

    return () => {
      console.log('🔴 Desconectando Realtime do calendário');
      supabase.removeChannel(channel);
    };
  }, [companyId]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      console.log('📅 Carregando eventos do Supabase...');
      
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_deleted', false)
        .order('start_date', { ascending: true });
      
      if (error) throw error;
      
      // Converter formato do banco para formato do frontend
      const formattedEvents = (data || []).map(event => ({
        id: event.id,
        title: event.title,
        description: event.description,
        startDate: event.start_date,
        endDate: event.end_date,
        type: event.event_type,
        project: event.project_id,
        color: event.color,
        icon: event.icon,
        location: event.location,
        attendees: event.participants || [],
        reminder: event.reminders?.[0]?.minutes_before || null,
        priority: event.priority,
        recurring: event.is_recurring ? 'custom' : 'none',
        allDay: event.all_day,
        custom_fields: event.custom_fields || null
      }));
      
      setEvents(formattedEvents);
      console.log(`✅ ${formattedEvents.length} eventos carregados`);
    } catch (error) {
      console.error('❌ Erro ao carregar eventos:', error);
      alert('Erro ao carregar eventos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', companyId)
        .eq('is_archived', false)
        .order('name');
      
      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startDate);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getEventsForWeek = (startDate) => {
    const weekEvents = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      weekEvents.push({
        date,
        events: getEventsForDate(date)
      });
    }
    return weekEvents;
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      meeting: 'groups',
      deadline: 'flag',
      task: 'task',
      event: 'event',
      milestone: 'stars',
      campaign: 'campaign'
    };
    return icons[type] || 'event';
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      meeting: 'Reunião',
      deadline: 'Prazo',
      task: 'Tarefa',
      event: 'Evento',
      milestone: 'Marco',
      campaign: 'Campanha'
    };
    return labels[type] || 'Evento';
  };

  const handleEventClick = (event) => {
    if (event.type === 'campaign') {
      const campaignId = event.custom_fields?.campaign_id;
      if (campaignId) {
        navigate(`/contatos?tab=campanhas&campaign_id=${campaignId}`);
        return;
      }
    }
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const filteredEvents = events.filter(event => {
    if (filterProject !== 'all' && event.project !== filterProject) return false;
    if (filterType !== 'all' && event.type !== filterType) return false;
    return true;
  });

  // Estatísticas
  const getStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayEvents = events.filter(e => {
      const eventDate = new Date(e.startDate);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === today.getTime();
    });

    const upcomingEvents = events.filter(e => {
      const eventDate = new Date(e.startDate);
      return eventDate > today;
    });

    const urgentEvents = events.filter(e => 
      e.priority === 'urgente' || e.priority === 'critica'
    );

    return {
      today: todayEvents.length,
      upcoming: upcomingEvents.length,
      urgent: urgentEvents.length,
      total: events.length
    };
  };

  const stats = getStats();

  const handleSaveEvent = async (eventData) => {
    try {
      // Converter formato do frontend para formato do banco
      const dbEvent = {
        company_id: companyId,
        title: eventData.title,
        description: eventData.description,
        start_date: eventData.startDate,
        end_date: eventData.endDate,
        all_day: eventData.allDay || false,
        event_type: eventData.type,
        priority: eventData.priority || 'normal',
        color: eventData.color,
        icon: eventData.icon,
        location: eventData.location,
        project_id: eventData.project,
        participants: eventData.attendees || [],
        reminders: eventData.reminder ? [{ minutes_before: eventData.reminder, type: 'email' }] : [],
        is_recurring: eventData.recurring && eventData.recurring !== 'none',
        organizer_id: user.id,
        created_by: user.id,
        updated_by: user.id
      };

      if (eventData.id && events.find(e => e.id === eventData.id)) {
        // Atualizar evento existente
        const { error } = await supabase
          .from('calendar_events')
          .update(dbEvent)
          .eq('id', eventData.id);
        
        if (error) throw error;
        console.log('✅ Evento atualizado');
      } else {
        // Criar novo evento
        const { error } = await supabase
          .from('calendar_events')
          .insert([dbEvent]);
        
        if (error) throw error;
        console.log('✅ Evento criado');
      }
      
      // Recarregar eventos
      await loadEvents();
    } catch (error) {
      console.error('❌ Erro ao salvar evento:', error);
      alert('Erro ao salvar evento: ' + error.message);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      // Soft delete
      const { error } = await supabase
        .from('calendar_events')
        .update({ 
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: user.id
        })
        .eq('id', eventId);
      
      if (error) throw error;
      
      console.log('✅ Evento deletado');
      await loadEvents();
    } catch (error) {
      console.error('❌ Erro ao deletar evento:', error);
      alert('Erro ao deletar evento: ' + error.message);
    }
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];
    
    // Dias do mês anterior
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayEvents = getEventsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => {
            setSelectedDate(date);
            setDayViewDate(date);
          }}
          onDoubleClick={() => {
            setDayViewDate(date);
            setView('day');
          }}
        >
          <div className="day-number">{day}</div>
          <div className="day-events">
            {dayEvents.map((event, index) => (
              <div
                key={event.id}
                className="day-event-icon"
                style={{ backgroundColor: event.color }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event);
                }}
                title={`${event.title}${event.startTime ? ' - ' + new Date(event.startDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}`}
              >
                <AppIcon name={getEventTypeIcon(event.type)} />
                {event.priority && event.priority !== 'normal' && (
                  <span className="priority-dot"></span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return days;
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const weekDays = [];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <div className="week-view">
        <div className="week-header">
          {Array.from({ length: 7 }, (_, i) => {
            const date = new Date(startOfWeek);
            date.setDate(date.getDate() + i);
            const isToday = date.toDateString() === new Date().toDateString();
            
            return (
              <div key={i} className={`week-day-header ${isToday ? 'today' : ''}`}>
                <div className="day-name">{['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][i]}</div>
                <div className="day-number">{date.getDate()}</div>
              </div>
            );
          })}
        </div>
        
        <div className="week-grid">
          <div className="time-column">
            {hours.map(hour => (
              <div key={hour} className="time-slot">
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>
          
          {Array.from({ length: 7 }, (_, dayIndex) => {
            const date = new Date(startOfWeek);
            date.setDate(date.getDate() + dayIndex);
            const dayEvents = getEventsForDate(date);
            
            return (
              <div key={dayIndex} className="week-day-column">
                {hours.map(hour => (
                  <div key={hour} className="hour-slot"></div>
                ))}
                {dayEvents.map(event => {
                  const startTime = new Date(event.startDate);
                  const topPosition = (startTime.getHours() + startTime.getMinutes() / 60) * 60;
                  const duration = (new Date(event.endDate) - startTime) / (1000 * 60);
                  const height = (duration / 60) * 60;
                  
                  return (
                    <div
                      key={event.id}
                      className="week-event"
                      style={{
                        top: `${topPosition}px`,
                        height: `${height}px`,
                        backgroundColor: event.color
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                      <strong>{event.title}</strong>
                      <small>{startTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</small>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const dayEvents = getEventsForDate(dayViewDate);
    
    const changeDayViewDate = (direction) => {
      const newDate = new Date(dayViewDate);
      newDate.setDate(newDate.getDate() + direction);
      setDayViewDate(newDate);
    };

    return (
      <div className="day-view">
        {/* Day Header */}
        <div className="day-view-header">
          <div className="day-navigation">
            <button onClick={() => changeDayViewDate(-1)}>
              <AppIcon name="chevron_left" />
            </button>
            <div className="day-info">
              <h2>{dayViewDate.toLocaleDateString('pt-BR', { weekday: 'long' })}</h2>
              <h3>{dayViewDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}</h3>
            </div>
            <button onClick={() => changeDayViewDate(1)}>
              <AppIcon name="chevron_right" />
            </button>
          </div>
          <div className="day-stats">
            <span className="day-event-count">
              <AppIcon name="event" />
              {dayEvents.length} evento(s)
            </span>
          </div>
        </div>

        {/* Timeline */}
        <div className="day-timeline">
          <div className="timeline-hours">
            {hours.map(hour => (
              <div key={hour} className="timeline-hour">
                <span className="hour-label">{hour.toString().padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>

          <div className="timeline-events">
            {hours.map(hour => (
              <div key={hour} className="timeline-slot"></div>
            ))}
            
            {/* Current time indicator */}
            {dayViewDate.toDateString() === new Date().toDateString() && (() => {
              const now = new Date();
              const topPosition = (now.getHours() + now.getMinutes() / 60) * 80;
              return (
                <div className="current-time-line" style={{ top: `${topPosition}px` }}>
                  <div className="time-dot"></div>
                  <div className="time-line"></div>
                </div>
              );
            })()}

            {/* Events */}
            {dayEvents.map(event => {
              const startTime = new Date(event.startDate);
              const endTime = new Date(event.endDate);
              const topPosition = (startTime.getHours() + startTime.getMinutes() / 60) * 80;
              const duration = (endTime - startTime) / (1000 * 60);
              const height = (duration / 60) * 80;
              
              return (
                <div
                  key={event.id}
                  className="timeline-event"
                  style={{
                    top: `${topPosition}px`,
                    height: `${Math.max(height, 40)}px`,
                    backgroundColor: event.color
                  }}
                  onClick={() => handleEventClick(event)}
                >
                  <div className="timeline-event-content">
                    <div className="timeline-event-header">
                      <AppIcon name={getEventTypeIcon(event.type)} />
                      <strong>{event.title}</strong>
                      {event.priority && event.priority !== 'normal' && (
                        <span className="timeline-priority">
                          {event.priority === 'urgente' && '🔴'}
                          {event.priority === 'alta' && '🟠'}
                          {event.priority === 'baixa' && '🟢'}
                        </span>
                      )}
                    </div>
                    <div className="timeline-event-time">
                      {startTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {' - '}
                      {endTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {event.location && (
                      <div className="timeline-event-location">
                        <AppIcon name="location_on" />
                        {event.location}
                      </div>
                    )}
                    {event.attendees && event.attendees.length > 0 && (
                      <div className="timeline-event-attendees">
                        <AppIcon name="group" />
                        {event.attendees.length} participante(s)
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Empty State */}
        {dayEvents.length === 0 && (
          <div className="day-empty-state">
            <AppIcon name="event_available" />
            <h3>Nenhum evento agendado</h3>
            <p>Clique em "Novo Evento" para adicionar um compromisso</p>
          </div>
        )}
      </div>
    );
  };

  const renderAgendaView = () => {
    const groupedEvents = {};
    
    filteredEvents.forEach(event => {
      const date = new Date(event.startDate).toLocaleDateString('pt-BR');
      if (!groupedEvents[date]) {
        groupedEvents[date] = [];
      }
      groupedEvents[date].push(event);
    });
    
    return (
      <div className="agenda-view">
        {Object.entries(groupedEvents).map(([date, dateEvents]) => (
          <div key={date} className="agenda-day">
            <div className="agenda-date">
              <h3>{date}</h3>
              <span>{dateEvents.length} evento(s)</span>
            </div>
            <div className="agenda-events">
              {dateEvents.map(event => (
                <div
                  key={event.id}
                  className="agenda-event"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="event-time">
                    {new Date(event.startDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="event-indicator" style={{ backgroundColor: event.color }}></div>
                  <div className="event-details">
                    <div className="event-header">
                      <AppIcon name={getEventTypeIcon(event.type)} />
                      <h4>{event.title}</h4>
                      <span className="event-type-badge">{getEventTypeLabel(event.type)}</span>
                    </div>
                    {event.description && <p>{event.description}</p>}
                    <div className="event-meta">
                      {event.project && <span>📁 {event.project}</span>}
                      {event.location && <span>📍 {event.location}</span>}
                      {event.attendees && <span>👥 {event.attendees.length} participante(s)</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="calendar-page">
        <LoadingSpinner message="Carregando calendário..." />
      </div>
    );
  }

  return (
    <div className="calendar-page">
      {/* Header */}
      <div className="calendar-header">
        <div className="header-left">
          <h1>
            <AppIcon name="calendar_month" />
            Calendário
          </h1>
          <p>Gerencie seus eventos, prazos e reuniões</p>
        </div>
        
        <button className="btn-add-event" onClick={() => {
          setSelectedEvent(null);
          setShowEventModal(true);
        }}>
          <AppIcon name="add" />
          Novo Evento
        </button>
      </div>

      {/* Stats Cards */}
      <div className="calendar-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <AppIcon name="today" />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.today}</span>
            <span className="stat-label">Hoje</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <AppIcon name="event_upcoming" />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.upcoming}</span>
            <span className="stat-label">Próximos</span>
          </div>
        </div>

        <div className="stat-card urgent">
          <div className="stat-icon">
            <AppIcon name="priority_high" />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.urgent}</span>
            <span className="stat-label">Urgentes</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <AppIcon name="event_note" />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="calendar-toolbar">
        <div className="toolbar-left">
          <button className="btn-today" onClick={goToToday}>Hoje</button>
          
          <div className="navigation-buttons">
            <button onClick={() => changeMonth(-1)}>
              <AppIcon name="chevron_left" />
            </button>
            <button onClick={() => changeMonth(1)}>
              <AppIcon name="chevron_right" />
            </button>
          </div>
          
          <h2 className="current-month">
            {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
          </h2>
        </div>
        
        <div className="toolbar-right">
          <div className="filter-group">
            <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
              <option value="all">Todos os Projetos</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">Todos os Tipos</option>
              <option value="meeting">Reuniões</option>
              <option value="deadline">Prazos</option>
              <option value="task">Tarefas</option>
              <option value="event">Eventos</option>
              <option value="milestone">Marcos</option>
            </select>
          </div>
          
          <div className="view-switcher">
            <button
              className={view === 'month' ? 'active' : ''}
              onClick={() => setView('month')}
              title="Visualização Mensal"
            >
              <AppIcon name="calendar_view_month" />
            </button>
            <button
              className={view === 'week' ? 'active' : ''}
              onClick={() => setView('week')}
              title="Visualização Semanal"
            >
              <AppIcon name="calendar_view_week" />
            </button>
            <button
              className={view === 'day' ? 'active' : ''}
              onClick={() => setView('day')}
              title="Visualização Diária"
            >
              <AppIcon name="view_day" />
            </button>
            <button
              className={view === 'agenda' ? 'active' : ''}
              onClick={() => setView('agenda')}
              title="Visualização Agenda"
            >
              <AppIcon name="view_agenda" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="calendar-content">
        {view === 'month' && (
          <div className="month-view">
            <div className="weekdays">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                <div key={day} className="weekday">{day}</div>
              ))}
            </div>
            <div className="calendar-grid">
              {renderMonthView()}
            </div>
          </div>
        )}
        
        {view === 'week' && renderWeekView()}
        
        {view === 'day' && renderDayView()}
        
        {view === 'agenda' && renderAgendaView()}
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          projects={projects}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
        />
      )}
    </div>
  );
}
